import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HELP_CONTACT,
  helpFaqs,
  helpFaqCategories,
} from '../content/helpContent';

type TabType = 'faq' | 'about';

export default function HelpCenter() {
  const [activeTab, setActiveTab] = useState<TabType>('faq');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFAQs = helpFaqs.filter((faq) => {
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (id: string) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-amber-800 mb-2">Help Center</h1>
          <p className="text-gray-600">Find answers to common questions</p>
          <Link
            to="/profile"
            className="inline-block mt-3 text-sm text-amber-700 font-semibold hover:underline"
          >
            ← Back to Profile
          </Link>
        </div>

        <div className="flex gap-4 mb-6 border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab('faq')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'faq'
                ? 'text-amber-700 border-b-2 border-amber-700'
                : 'text-gray-600 hover:text-amber-700'
            }`}
          >
            FAQs
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('about')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'about'
                ? 'text-amber-700 border-b-2 border-amber-700'
                : 'text-gray-600 hover:text-amber-700'
            }`}
          >
            About App
          </button>
        </div>

        {activeTab === 'faq' ? (
          <>
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for help..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <span className="absolute left-4 top-3.5 text-gray-400">🔍</span>
              </div>
            </div>

            <div className="mb-6 overflow-x-auto hide-scrollbar">
              <div className="flex gap-2 pb-2">
                {helpFaqCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                      selectedCategory === category
                        ? 'bg-amber-700 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3 mb-8">
              {filteredFAQs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No FAQs found. Try a different search term or category.</p>
                </div>
              ) : (
                filteredFAQs.map((faq) => (
                  <div
                    key={faq.id}
                    className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleFAQ(faq.id)}
                      className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition"
                    >
                      <div className="flex-1">
                        <span className="text-xs text-amber-700 font-semibold mr-2">{faq.category}</span>
                        <h3 className="text-base md:text-lg font-semibold text-gray-800 mt-1">
                          {faq.question}
                        </h3>
                      </div>
                      <span className="text-amber-700 text-xl ml-4">
                        {expandedFAQ === faq.id ? '−' : '+'}
                      </span>
                    </button>
                    {expandedFAQ === faq.id && (
                      <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                        <p className="text-gray-600 text-sm md:text-base leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-amber-800 mb-4">Still need help?</h2>
              <p className="text-sm text-gray-600 mb-4">
                You can contact us anytime through email, phone ({HELP_CONTACT.hours}), or send
                feedback — like Talabat&apos;s help options.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📧</span>
                  <div>
                    <p className="font-semibold text-gray-800">Email Support</p>
                    <a href={`mailto:${HELP_CONTACT.email}`} className="text-sm text-amber-700 hover:underline">
                      {HELP_CONTACT.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📞</span>
                  <div>
                    <p className="font-semibold text-gray-800">Phone Support</p>
                    <p className="text-sm text-amber-700">{HELP_CONTACT.phoneDisplay}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💬</span>
                  <div>
                    <p className="font-semibold text-gray-800">Feedback</p>
                    <a
                      href={`mailto:${HELP_CONTACT.feedbackEmail}`}
                      className="text-sm text-amber-700 hover:underline"
                    >
                      {HELP_CONTACT.feedbackEmail}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link
                to="/orders"
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition text-center"
              >
                <span className="text-3xl mb-2 block">📋</span>
                <h3 className="font-semibold text-gray-800">View Orders</h3>
                <p className="text-sm text-gray-600">Check your order history</p>
              </Link>
              <Link
                to="/profile"
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition text-center"
              >
                <span className="text-3xl mb-2 block">👤</span>
                <h3 className="font-semibold text-gray-800">My Profile</h3>
                <p className="text-sm text-gray-600">Get Help & account settings</p>
              </Link>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="text-center mb-6">
                <span className="text-6xl mb-4 block">☕</span>
                <h2 className="text-2xl font-bold text-amber-800 mb-2">QahwaHub</h2>
                <p className="text-gray-600">Version 1.0.0</p>
              </div>
              <p className="text-gray-700 text-center leading-relaxed mb-4">
                Your favorite coffee ordering app. Discover cafés, order your favorite drinks, earn
                points, and enjoy premium benefits. Made with ❤️ in Jordan.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-amber-800 mb-4">App Features</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-xl">📍</span>
                  <div>
                    <p className="font-semibold text-gray-800">Find Cafés on Your Route</p>
                    <p className="text-sm text-gray-600">Discover cafés along your daily commute</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-xl">⭐</span>
                  <div>
                    <p className="font-semibold text-gray-800">Points & Rewards</p>
                    <p className="text-sm text-gray-600">Earn points with every order, redeem for free drinks</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-xl">💳</span>
                  <div>
                    <p className="font-semibold text-gray-800">Multiple Payment Options</p>
                    <p className="text-sm text-gray-600">Pay with Visa or Cash</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-xl">📱</span>
                  <div>
                    <p className="font-semibold text-gray-800">Real-time Order Updates</p>
                    <p className="text-sm text-gray-600">Get notifications when your order status changes</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-amber-800 mb-4">Legal</h3>
              <div className="space-y-3">
                <Link
                  to="/terms"
                  className="w-full text-left py-2 px-4 hover:bg-gray-50 rounded transition flex items-center justify-between"
                >
                  <span className="text-gray-700">Terms & Conditions</span>
                  <span className="text-amber-700">→</span>
                </Link>
                <Link
                  to="/privacy"
                  className="w-full text-left py-2 px-4 hover:bg-gray-50 rounded transition flex items-center justify-between"
                >
                  <span className="text-gray-700">Privacy Policy</span>
                  <span className="text-amber-700">→</span>
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-amber-800 mb-4">Company Information</h3>
              <div className="space-y-3 text-gray-700">
                <div>
                  <p className="font-semibold mb-1">QahwaHub</p>
                  <p className="text-sm text-gray-600">Amman, Jordan</p>
                </div>
                <div>
                  <p className="font-semibold mb-1">Contact</p>
                  <p className="text-sm text-gray-600">{HELP_CONTACT.email}</p>
                  <p className="text-sm text-gray-600">{HELP_CONTACT.phoneDisplay}</p>
                </div>
              </div>
            </div>

            <div className="text-center text-sm text-gray-500 py-4">
              <p>© {new Date().getFullYear()} QahwaHub. All rights reserved.</p>
              <p className="mt-1">Made with ☕ in Jordan</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
